"use client"
import React, { ReactNode, useRef } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Coins, LockIcon, Megaphone } from "lucide-react"
import { motion, useInView } from "framer-motion"
import {
  cardVariants,
  containerVariants,
  standardScrollConfig,
} from "@/utils/animations"
import SectionHeader from "./SectionHeader"
import SectionSubheader from "./SectionSubheader"

function RightCard({
  title,
  body,
  icon,
  index,
}: {
  title: string
  body: ReactNode
  icon: ReactNode
  index: number
}) {
  return (
    <motion.div
      className="bg-ivory px-6 py-6 rounded-lg"
      variants={cardVariants}
      custom={index}
      transition={{ duration: 0.5, delay: index * 0.2 }}
    >
      <div className="relative space-y-5">
        {icon}
        <Card className="border-none shadow-none leading-relaxed tracking-normal bg-inherit">
          <CardContent className="p-0">
            <h3 className="leading-[32px] font-ivyora text-2xl mb-1 tracking-[-0.72px]">
              {title}
            </h3>
            <p className="text-base text-muted-foreground">{body}</p>
          </CardContent>
        </Card>
      </div>
    </motion.div>
  )
}

function WriteRightColumn() {
  return (
    <>
      <RightCard
        icon={<Coins className="border rounded shadow-sm p-2" size={40} />}
        title="Earn, one post at a time"
        body="New ways for writers to earn, beyond paywalls & ads."
        index={0}
      />

      <RightCard
        icon={<Megaphone className="border rounded shadow-sm p-2" size={40} />}
        title="Reach more readers"
        body="Grow your audience by sharing seamlessly across social & more."
        index={1}
      />

      <RightCard
        icon={<LockIcon className="border rounded shadow-sm p-2" size={40} />}
        title="Own your audience & writing"
        body="No more platform-dependent followers - readers can subscribe via email or wallet."
        index={2}
      />
    </>
  )
}

// SVG Background Component
const BackgroundSVG = () => (
  <div className="absolute inset-0 w-full h-full overflow-hidden -z-10">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      viewBox="0 0 1440 540"
      fill="none"
      preserveAspectRatio="xMidYMid slice"
      className="opacity-[0.05]"
    >
      <path
        d="M-80 -326H1520M-80 -286H1520M-80 -246H1520M-80 -206H1520M-80 -166H1520M-80 -126H1520M-80 -86.0002H1520M-80 -46.0002H1520M-80 -6.00024H1520M-80 33.9998H1520M-80 73.9998H1520M-80 114H1520M-80 154H1520M-80 194H1520M-80 234H1520M-80 274H1520M-80 314H1520M-80 354H1520M-80 394H1520M-80 434H1520M-80 474H1520M-80 514H1520M-80 554H1520M-80 594H1520M-80 634H1520M-80 674H1520M-80 714H1520M-80 754H1520M-80 794H1520M-80 834H1520M-80 874H1520M-80 914H1520M-80 954H1520M-80 994H1520M-80 1034H1520M-80 1074H1520M-80 1114H1520M-80 1154H1520M-80 1194H1520M-80 1234H1520"
        stroke="#26251A"
      />
    </svg>
  </div>
)

/**
 * A landing page section with a sticky "Why write?" heading and scrollable content
 *
 * @component
 */
export const WhyWriteSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, standardScrollConfig)

  return (
    <div className="relative">
      {/* Added SVG Background */}
      <BackgroundSVG />
      <div ref={sectionRef} className="container mx-auto py-24 relative">
        <SectionHeader className="bg-white" ref={sectionRef}>
          Why Paragraph?
        </SectionHeader>

        <SectionSubheader className="bg-white" ref={sectionRef}>
          Paragraph makes it easy to publish your ideas, reach readers
          everywhere, and unlock new opportunities to earn directly from your
          work.
        </SectionSubheader>
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-10"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Scrollable right column */}
          <WriteRightColumn />
        </motion.div>
      </div>
    </div>
  )
}
